import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API_BASE });

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("crm_token", token);
  } else {
    delete api.defaults.headers.common["Authorization"];
    localStorage.removeItem("crm_token");
  }
}

const saved = localStorage.getItem("crm_token");
if (saved) api.defaults.headers.common["Authorization"] = `Bearer ${saved}`;

// Robuuste, cross-browser & Safari-proof download voor geauthenticeerde bestanden.
// We halen het bestand op als blob mét de Authorization-header (geen token in de URL nodig,
// werkt dus ook voor endpoints die alleen header-auth kennen), maken er een same-origin
// blob:-URL van en klikken een anker met een expliciet `download`-attribuut. Cleanup via
// setTimeout — een directe revoke ná click annuleert de download in Safari.
function _filenameFromCD(cd, fallback) {
  if (!cd) return fallback || "download";
  const m = /filename\*?=(?:UTF-8'')?["']?([^;"'\n]+)/i.exec(cd);
  try { return m ? decodeURIComponent(m[1]) : (fallback || "download"); }
  catch { return fallback || "download"; }
}

function _triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
}

export async function downloadFile(path, fallbackName, config = {}) {
  try {
    const res = await api.get(path, { ...config, responseType: "blob" });
    const name = fallbackName || _filenameFromCD(res.headers?.["content-disposition"], fallbackName);
    const type = res.headers?.["content-type"] || "application/octet-stream";
    _triggerBlobDownload(new Blob([res.data], { type }), name);
    return true;
  } catch (err) {
    let msg = "Download mislukt";
    try {
      if (err?.response?.data instanceof Blob) {
        const txt = await err.response.data.text();
        const j = JSON.parse(txt);
        if (j?.detail) msg = j.detail;
      }
    } catch { /* ignore */ }
    toast.error(msg);
    return false;
  }
}

// Behoudt bestaande aanroepen; gebruikt nu dezelfde Safari-proof blob-download.
export function downloadUrl(path, fallbackName) {
  downloadFile(path, fallbackName);
}

// Download een base64-string als bestand (Safari-proof).
export function downloadBase64(b64, filename, mime = "application/pdf") {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    _triggerBlobDownload(new Blob([bytes], { type: mime }), filename);
    return true;
  } catch {
    toast.error("Bestand openen mislukt");
    return false;
  }
}

// Safari-proof download via POST (bijv. batch-PDF met body). Stuurt JSON, ontvangt blob.
export async function postDownload(path, body, fallbackName) {
  try {
    const res = await api.post(path, body, { responseType: "blob" });
    const name = fallbackName || _filenameFromCD(res.headers?.["content-disposition"], fallbackName);
    const type = res.headers?.["content-type"] || "application/octet-stream";
    _triggerBlobDownload(new Blob([res.data], { type }), name);
    return true;
  } catch (err) {
    let msg = "Download mislukt";
    try {
      if (err?.response?.data instanceof Blob) {
        const txt = await err.response.data.text();
        const j = JSON.parse(txt);
        if (j?.detail) msg = j.detail;
      }
    } catch { /* ignore */ }
    toast.error(msg);
    return false;
  }
}
